// apps/roleforge-app/src/components/forge/ForgeSynthesisBurst.tsx
//
// Plan 3e-ii — Task 6.4: Animated overlay shown over the left panel area
// while the agent fires its synthesis tool calls in rapid succession.
//
// Subscribes to operating_model SSE events and advances through the
// FORGE_BURST_TIMELINE phases. When the trigger event for the current phase
// arrives AND that phase's minDurationMs has elapsed since it started, we
// advance to the next phase. Advancing past 'roi' calls onDone().
//
// The right-panel narration (AgentActivityPanel) runs in lockstep on its
// own — driven by the agent's narrate_action calls, not by this component.
//
// We use plain Tailwind CSS animations rather than a motion library to keep
// dependencies small and to side-step React 19 / framer-motion v11 ABI
// mismatches.

import { useEffect, useRef, useState } from 'react';
import type { SseEventRouter } from '../../lib/sse-event-router';
import type { OperatingModelEventPayload } from '../../lib/converse-types';
import {
  FORGE_BURST_TIMELINE,
  type ForgeBurstPhase,
} from './forge-burst-timeline';

interface Props {
  router: SseEventRouter;
  onDone: () => void;
}

function nextPhase(p: ForgeBurstPhase): ForgeBurstPhase {
  const idx = FORGE_BURST_TIMELINE.findIndex((s) => s.phase === p);
  return FORGE_BURST_TIMELINE[idx + 1]?.phase ?? 'done';
}

export function ForgeSynthesisBurst({ router, onDone }: Props) {
  const [phase, setPhase] = useState<ForgeBurstPhase>('engines');
  const [provenance, setProvenance] = useState<string | null>(null);
  const phaseStartedAt = useRef<number>(Date.now());
  // Track the latest phase inside subscription handlers without
  // re-subscribing on every state change.
  const phaseRef = useRef<ForgeBurstPhase>('engines');
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  useEffect(() => {
    const unsubs = FORGE_BURST_TIMELINE.map((spec) =>
      router.subscribe(
        'operating_model',
        spec.triggerEvent as OperatingModelEventPayload['kind'],
        (event) => {
          // Capture provenance citation if the payload carries one.
          const payload = event.payload as { source_quote?: unknown };
          if (typeof payload.source_quote === 'string') {
            const quote = payload.source_quote;
            setProvenance(quote);
            setTimeout(() => {
              setProvenance((cur) => (cur === quote ? null : cur));
            }, 2000);
          }

          // Advance phase if this event matches the current phase's trigger
          // AND the phase's minimum display duration has elapsed.
          if (phaseRef.current !== spec.phase) return;
          const elapsed = Date.now() - phaseStartedAt.current;
          if (elapsed < spec.minDurationMs) return;

          const next = nextPhase(spec.phase);
          if (next === 'done') {
            onDone();
          } else {
            phaseStartedAt.current = Date.now();
            setPhase(next);
          }
        },
      ),
    );
    return () => unsubs.forEach((u) => u());
  }, [router, onDone]);

  const cue =
    FORGE_BURST_TIMELINE.find((s) => s.phase === phase)?.narrationCue ?? '';

  return (
    <div
      data-testid="forge-synthesis-burst"
      className="absolute inset-0 pointer-events-none flex items-center justify-center bg-white/40 dark:bg-black/40 backdrop-blur-sm"
    >
      <div
        // Re-keying on phase forces React to remount, which restarts the
        // CSS fade-in animation cleanly each time the phase advances.
        key={phase}
        className="rounded-lg bg-card shadow-md px-5 py-3 text-sm text-slate-700 dark:text-slate-300 max-w-md text-center transition-opacity duration-300 animate-[fadeIn_0.4s_ease-out]"
        style={{ animation: 'forgeBurstFadeIn 400ms ease-out' }}
      >
        <span
          aria-hidden="true"
          className="mr-2 inline-block h-2 w-2 animate-ping rounded-full bg-amber-400 align-middle"
        />
        {cue}
      </div>

      {provenance && (
        <div
          key={provenance}
          className="absolute bottom-6 left-6 right-6 text-center text-xs italic text-slate-500"
          style={{ animation: 'forgeBurstFadeIn 300ms ease-out' }}
        >
          {provenance}
        </div>
      )}

      {/* Inline keyframes so the component is self-contained and the
          animation works without requiring a global Tailwind config tweak. */}
      <style>{`
        @keyframes forgeBurstFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
